/**
 * Users HTTP service (PX-W6.2) — every users/members call this ticket's
 * surfaces make, over the ONE shared transport. Paths carry the `{orgId}`
 * placeholder the W6.1 interceptor replaces.
 *
 * INV-1: no body declared here carries `members[]`, an ACL array or an
 * `organisations` array. Roster and access changes go through the dedicated
 * verbs below (`role`, `type`, `group`, activate/deactivate) — never a
 * wholesale entity PUT.
 */

import { apiClient } from '@/services'

import type {
  ApiActivityEntry,
  ApiInvite,
  ApiProjectSummary,
  ApiUser,
  InviteInput,
  MemberRow,
  OrgRole,
  ProjectInviteInput,
  UserUpdateInput
} from './types'

export const usersHttp = {
  /** The org's user directory. */
  list(): Promise<ApiUser[]> {
    return apiClient.get<ApiUser[]>('/{orgId}/users')
  },

  /** One user record. */
  get(id: string): Promise<ApiUser> {
    return apiClient.get<ApiUser>(`/{orgId}/users/${id}`)
  },

  /** Update the self-writeable/admin-gated display fields of one user. */
  update(id: string, body: UserUpdateInput): Promise<ApiUser> {
    return apiClient.put<ApiUser>(`/{orgId}/users/${id}`, body)
  },

  /** Invite a user (onboarding step 02). `owner` is accepted from an owner caller only (server-enforced). */
  invite(body: InviteInput): Promise<ApiInvite> {
    return apiClient.post<ApiInvite>('/{orgId}/users/invite', body)
  },

  /** Pending invites (the token never serialises). */
  listInvites(): Promise<ApiInvite[]> {
    return apiClient.get<ApiInvite[]>('/{orgId}/users/invites')
  },

  resendInvite(inviteId: string): Promise<ApiInvite> {
    return apiClient.post<ApiInvite>(`/{orgId}/users/invites/${inviteId}/resend`)
  },

  revokeInvite(inviteId: string): Promise<void> {
    return apiClient.delete<void>(`/{orgId}/users/invites/${inviteId}`)
  },

  /** Invite one address to a project (org admin + project WRITE, server-enforced). */
  inviteToProject(projectId: string, body: ProjectInviteInput): Promise<ApiInvite> {
    return apiClient.post<ApiInvite>(`/{orgId}/projects/${projectId}/members/invite`, body)
  },

  /** A project's pending invites (same gate as the writes — ruling OQ-1). */
  listProjectInvites(projectId: string): Promise<ApiInvite[]> {
    return apiClient.get<ApiInvite[]>(`/{orgId}/projects/${projectId}/members/invites`)
  },

  /** Resend rotates the link: the answer is a NEW row with a new id. */
  resendProjectInvite(projectId: string, inviteId: string): Promise<ApiInvite> {
    return apiClient.post<ApiInvite>(`/{orgId}/projects/${projectId}/members/invites/${inviteId}/resend`)
  },

  revokeProjectInvite(projectId: string, inviteId: string): Promise<void> {
    return apiClient.delete<void>(`/{orgId}/projects/${projectId}/members/invites/${inviteId}`)
  },

  /** Change a user's org role (orgAdmin gate server-side). */
  changeRole(userId: string, role: OrgRole): Promise<ApiUser> {
    return apiClient.put<ApiUser>(`/{orgId}/users/${userId}/role`, { role })
  },

  deactivate(userId: string): Promise<ApiUser> {
    return apiClient.put<ApiUser>(`/{orgId}/users/${userId}/deactivate`)
  },

  activate(userId: string): Promise<ApiUser> {
    return apiClient.put<ApiUser>(`/{orgId}/users/${userId}/activate`)
  },

  /** A user's recent activity rows. */
  activity(userId: string, limit = 20, offset = 0): Promise<ApiActivityEntry[]> {
    return apiClient.get<ApiActivityEntry[]>(`/{orgId}/users/${userId}/activity`, {
      params: { limit, offset }
    })
  },

  /** The members of one project (FIRM/CLIENT side as the server declared). */
  projectMembers(projectId: string): Promise<MemberRow[]> {
    return apiClient.get<MemberRow[]>(`/{orgId}/projects/${projectId}/members`)
  },

  /** The projects feeding the members browser's selector (read-only). */
  orgProjects(): Promise<ApiProjectSummary[]> {
    return apiClient.get<ApiProjectSummary[]>('/{orgId}/projects')
  }
}
